import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import {
  mergeParticipants,
  summarizeParticipants,
  filterParticipants,
  EMPTY_PARTICIPANT_FILTER,
  CATEGORY_ALL_GUESTS,
  MEMBER_CATEGORY_CODE,
  PARTICIPATION_MODE_LABELS,
  type Participant,
  type ParticipantFilter,
  type ApplicationRow,
  type GuestRow,
} from "../../../shared/event-participants";
import { fmtDateTime } from "../../lib/format";
import { ThumbnailUploader } from "../../components/ThumbnailUploader";
import { RichTextEditor } from "../../components/RichTextEditor";
import {
  SectionHeading,
  StatusBadge,
  ErrorBanner,
  SuccessBanner,
  Banner,
} from "../../components/ui";

interface Row {
  id: string;
  slug: string;
  title: string;
  status: string;
  eventStartAt: string | null;
  appliedCount?: number;
  socialCount?: number;
  capacityTotal?: number | null;
  hasSocial?: number;
}

const EVENT_STATUS_LABELS: Record<string, string> = {
  draft: "下書き",
  published: "公開",
  closed: "締切",
  completed: "完了",
  archived: "アーカイブ",
};
const EVENT_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  draft: "neutral",
  published: "success",
  closed: "warning",
  completed: "neutral",
  archived: "neutral",
};

type Status = "draft" | "published" | "closed" | "completed" | "archived";
type NotifyPreset =
  | "none"
  | "1d_10"
  | "1d_18"
  | "2d_10"
  | "day_9"
  | "custom";

const NOTIFY_PRESET_LABELS: Record<NotifyPreset, string> = {
  none: "送信しない",
  "1d_10": "1 日前 10:00",
  "1d_18": "1 日前 18:00",
  "2d_10": "2 日前 10:00",
  day_9: "当日朝 9:00",
  custom: "カスタム (日時指定)",
};

type PartMode = "offline" | "online" | "hybrid";

interface Form {
  id: string | null;
  title: string;
  slug: string;
  summary: string;
  description: string;
  eventStartAt: string;
  publishAt: string;
  capacityTotal: string;
  status: Status;
  participationMode: PartMode;
  thumbnailKey: string | null;
  // LINE リマインダー (Phase 2)
  notifyLinePreset: NotifyPreset;
  notifyLineCustomAt: string;
  notifyLineAtFromServer: string | null;
  notifiedLineAt: string | null;
  hasSocial: boolean;
  socialCapacity: string;
  socialFee: string;
  socialVenue: string;
  socialStartAt: string;
  cancelDeadlineAt: string;
  surveyNotifyAt: string;
}

const INITIAL: Form = {
  id: null,
  title: "",
  slug: "",
  summary: "",
  description: "",
  eventStartAt: "",
  publishAt: "",
  capacityTotal: "",
  status: "draft",
  participationMode: "offline",
  thumbnailKey: null,
  notifyLinePreset: "none",
  notifyLineCustomAt: "",
  notifyLineAtFromServer: null,
  notifiedLineAt: null,
  hasSocial: false,
  socialCapacity: "",
  socialFee: "0",
  socialVenue: "",
  socialStartAt: "",
  cancelDeadlineAt: "",
  surveyNotifyAt: "",
};

const JST_OFFSET_MS = 9 * 3600000;

// サーバ返却値 (ISO / naive UTC) を <input type=datetime-local> の "YYYY-MM-DDTHH:mm" に整形 (JST 固定)
function toDatetimeLocal(v: string | null): string {
  if (!v) return "";
  const s = String(v).trim();
  const hasTz = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasTz ? s : s.replace(" ", "T") + "Z");
  if (isNaN(d.getTime())) return "";
  const jst = new Date(d.getTime() + JST_OFFSET_MS);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${jst.getUTCFullYear()}-${pad(jst.getUTCMonth() + 1)}-${pad(jst.getUTCDate())}T${pad(jst.getUTCHours())}:${pad(jst.getUTCMinutes())}`;
}

// datetime-local (JST) → ISO (UTC)
function datetimeLocalToIso(v: string): string | null {
  if (!v) return null;
  // datetime-local は "2026-06-24T20:46" 形式 (TZ 無し、ユーザーの意図は JST)
  // JST → UTC に変換: JST - 9h = UTC
  const jstMs = new Date(v).getTime();
  if (isNaN(jstMs)) return null;
  return new Date(jstMs).toISOString();
}

// preset + eventStart (datetime-local=JST) からプレビュー文字列を計算 (JST 固定)
function computeClientPreview(
  preset: NotifyPreset,
  eventStartLocal: string,
  customAtLocal: string,
): string {
  if (preset === "none") return "送信しません";
  if (preset === "custom") {
    if (!customAtLocal) return "日時を指定してください";
    const d = new Date(customAtLocal);
    if (isNaN(d.getTime())) return "日時が不正です";
    return d.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
  }
  if (!eventStartLocal) return "開始日時を先に入力してください";
  const start = new Date(eventStartLocal);
  if (isNaN(start.getTime())) return "開始日時が不正です";
  // datetime-local はブラウザのTZで解釈されるが、preset計算は
  // 「イベント開始の N 日前 HH:00 JST」を意味する。
  // JST view を UTC で組み立てて toLocaleString(Asia/Tokyo) で表示する。
  const jstView = new Date(start.getTime() + JST_OFFSET_MS);
  const Y = jstView.getUTCFullYear();
  const M = jstView.getUTCMonth();
  const D = jstView.getUTCDate();
  let targetUtcMs: number;
  switch (preset) {
    case "1d_10":
      targetUtcMs = Date.UTC(Y, M, D - 1, 10, 0) - JST_OFFSET_MS;
      break;
    case "1d_18":
      targetUtcMs = Date.UTC(Y, M, D - 1, 18, 0) - JST_OFFSET_MS;
      break;
    case "2d_10":
      targetUtcMs = Date.UTC(Y, M, D - 2, 10, 0) - JST_OFFSET_MS;
      break;
    case "day_9":
      targetUtcMs = Date.UTC(Y, M, D, 9, 0) - JST_OFFSET_MS;
      break;
    default:
      return "不明なプリセット";
  }
  return new Date(targetUtcMs).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
}

interface MyaspConfig {
  webhookUrls: { user: string; eventEntry: string };
  token: string;
}

function buildMyaspTemplate(
  cfg: MyaspConfig,
  eventId: string,
  scenarioCode: string,
  entryType: "event" | "social",
): { url: string; data: string } {
  const url = cfg.webhookUrls.eventEntry;
  const lines = [
    `data[Entry][user_id]=%user_id%`,
    `data[Entry][mail]=%mail%`,
    `data[Entry][name1]=%name1%`,
    `data[Entry][name2]=%name2%`,
    `data[Entry][status]=1`,
    `data[Entry][scenario_type]=${entryType}`,
    `data[Entry][scenario_code]=${scenarioCode}`,
    `data[Entry][event_id]=${eventId}`,
    `data[Entry][entry_type]=${entryType}`,
    `data[Entry][amount]=%price%`,
    `data[Entry][posted_at]=%regist_date%`,
    `data[Entry][source]=myasp`,
    `data[Auth][token]=${cfg.token}`,
  ];
  return { url, data: lines.join("\n") };
}

// 参加者一覧の型・集計・絞り込みは shared/event-participants を正本とする。
// サーバの CSV も同じ関数を使うため、画面と CSV で数字がずれない。
type AppItem = ApplicationRow & { id: string };
type GuestItem = GuestRow;

function AppSummary({ list }: { list: Participant[] }) {
  const s = summarizeParticipants(list);
  return (
    <div className="text-xs text-tora-ink/70 mb-2 space-y-0.5">
      <p>
        参加: <b>{s.attending}</b>名 (受付済: <b>{s.checkedIn}</b>名)
        {s.social > 0 && (
          <span className="ml-2">
            / 懇親会: <b>{s.social}</b>名 (受付済: <b>{s.socialCheckedIn}</b>名)
          </span>
        )}
      </p>
      <p className="text-tora-ink/50">
        来場: {s.offline}名 (受付済 <b className="text-tora-ink/80">{s.offlineCheckedIn}</b>名 / 未受付 {s.offlineNotCheckedIn}名)
        <span className="ml-2">/ オンライン: {s.online}名</span>
        {s.guests > 0 && <span className="ml-2">/ ゲスト: {s.guests}名</span>}
        {s.walkIns > 0 && <span className="ml-2">/ 当日: {s.walkIns}名</span>}
        <span className="ml-2">/ キャンセル等: {s.inactive}名</span>
      </p>
    </div>
  );
}

function AppFilterBar({
  value,
  onChange,
  categories,
  total,
  shown,
  csvHref,
}: {
  value: ParticipantFilter;
  onChange: (f: ParticipantFilter) => void;
  categories: { code: string; name: string }[];
  total: number;
  shown: number;
  csvHref: string;
}) {
  const set = (patch: Partial<ParticipantFilter>) => onChange({ ...value, ...patch });
  const sel = "rounded border border-black/15 bg-white px-2 py-1 text-xs";
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <input
        className={`${sel} min-w-[180px] flex-1`}
        placeholder="名前 / カナ / 会員番号 / メールで検索"
        value={value.q}
        onChange={(e) => set({ q: e.target.value })}
      />
      <select className={sel} value={value.status} onChange={(e) => set({ status: e.target.value })}>
        <option value="attending">参加予定のみ</option>
        <option value="">すべて</option>
        <option value="walk_in">当日申込のみ</option>
        <option value="inactive">キャンセル・不参加</option>
      </select>
      <select className={sel} value={value.category} onChange={(e) => set({ category: e.target.value })}>
        <option value="">区分: すべて</option>
        <option value={MEMBER_CATEGORY_CODE}>会員</option>
        <option value={CATEGORY_ALL_GUESTS}>ゲスト全体</option>
        {categories.map((c) => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
      </select>
      <select className={sel} value={value.mode} onChange={(e) => set({ mode: e.target.value })}>
        <option value="">参加方法: すべて</option>
        <option value="offline">来場のみ</option>
        <option value="online">オンラインのみ</option>
      </select>
      <select className={sel} value={value.social} onChange={(e) => set({ social: e.target.value })}>
        <option value="">懇親会: すべて</option>
        <option value="yes">懇親会あり</option>
        <option value="no">懇親会なし</option>
      </select>
      <select className={sel} value={value.checkin} onChange={(e) => set({ checkin: e.target.value })}>
        <option value="">受付: すべて</option>
        <option value="done">イベント受付済</option>
        <option value="yet">イベント未受付</option>
        <option value="social_done">懇親会受付済</option>
        <option value="social_yet">懇親会 申込済で未受付</option>
      </select>
      <button
        type="button"
        className="text-xs underline text-tora-ink/60"
        onClick={() => onChange(EMPTY_PARTICIPANT_FILTER)}
      >
        条件クリア
      </button>
      <span className="text-xs font-bold text-tora-ink">
        該当 {shown} / {total} 名
      </span>
      <a href={csvHref} download className="btn-outline text-xs ml-auto">
        ⬇ CSV ダウンロード
      </a>
    </div>
  );
}

const STATUS_TONE: Record<string, "success" | "warning" | "danger" | "neutral"> = {
  申込中: "success",
  受付済: "success",
  参加: "success",
  決済待ち: "warning",
  キャンセル: "neutral",
  不参加: "danger",
};

// 当日参加の受付フォーム (会員 / 非会員ゲスト)。
// 会員は event_applications に walk_in=1 で作り、
// ゲストは event_guests に登録する。受付そのものは一覧の「受付する」で行う。
function WalkInPanel({
  eventId,
  categories,
  onDone,
  onError,
}: {
  eventId: string;
  categories: { code: string; name: string }[];
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState<"none" | "member" | "guest">("none");
  const [busy, setBusy] = useState(false);
  // 会員検索
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<{ id: string; displayName: string; memberNo: string }[]>([]);
  const [searched, setSearched] = useState(false);
  // 共通の参加条件
  const [mode, setMode] = useState<"offline" | "online">("offline");
  const [withSocial, setWithSocial] = useState(false);
  // ゲスト入力
  const [gName, setGName] = useState("");
  const [gKana, setGKana] = useState("");
  const [gCategory, setGCategory] = useState("");
  const [gNote, setGNote] = useState("");

  const reset = () => {
    setOpen("none"); setQ(""); setHits([]); setSearched(false);
    setMode("offline"); setWithSocial(false);
    setGName(""); setGKana(""); setGCategory(""); setGNote("");
  };

  const searchMembers = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await api.get<{ items: { id: string; displayName: string; memberNo: string }[] }>(
        `/admin/members?q=${encodeURIComponent(q.trim())}&perPage=20`,
      );
      setHits(r.items);
      setSearched(true);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addMember = async (memberId: string, name: string) => {
    setBusy(true);
    try {
      await api.post(`/admin/events/${eventId}/applications`, {
        memberId,
        participationMode: mode,
        withSocial,
      });
      reset();
      onDone(`${name} さんを当日申込として登録しました`);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addGuest = async () => {
    if (!gName.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/events/${eventId}/guests`, {
        name: gName.trim(),
        kana: gKana.trim() || null,
        categoryCode: gCategory || null,
        participationMode: mode,
        withSocial,
        note: gNote.trim() || null,
      });
      const name = gName.trim();
      reset();
      onDone(`${name} さんをゲストとして登録しました`);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sel = "rounded border border-black/15 bg-white px-2 py-1 text-xs";

  if (open === "none")
    return (
      <div className="flex flex-wrap gap-2 mb-2">
        <button className="btn-outline text-xs" onClick={() => setOpen("member")}>
          + 当日申込 (会員)
        </button>
        <button className="btn-outline text-xs" onClick={() => setOpen("guest")}>
          + ゲストを追加 (非会員)
        </button>
      </div>
    );

  return (
    <div className="mb-3 rounded border border-black/15 bg-black/5 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <b className="text-xs">
          {open === "member" ? "当日申込 (会員)" : "ゲストを追加 (非会員)"}
        </b>
        <button className="text-xs underline text-tora-ink/60" onClick={reset}>
          閉じる
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs">
        <select className={sel} value={mode} onChange={(e) => setMode(e.target.value as "offline" | "online")}>
          <option value="offline">来場</option>
          <option value="online">オンライン</option>
        </select>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={withSocial} onChange={(e) => setWithSocial(e.target.checked)} />
          懇親会に参加
        </label>
      </div>

      {open === "member" ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <input
              className={`${sel} min-w-[220px] flex-1`}
              placeholder="会員を名前・会員番号・メールで検索"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchMembers())}
              autoFocus
            />
            <button className="btn-outline text-xs" onClick={searchMembers} disabled={busy || !q.trim()}>
              検索
            </button>
          </div>
          {searched && hits.length === 0 && (
            <p className="text-xs text-tora-ink/50">該当する会員がいません</p>
          )}
          {hits.length > 0 && (
            <ul className="divide-y divide-black/10 rounded border border-black/10 bg-white">
              {hits.map((m) => (
                <li key={m.id} className="flex items-center justify-between p-1.5 text-xs">
                  <span>
                    <span className="font-mono text-tora-ink/60">{m.memberNo}</span>{" "}
                    {m.displayName}
                  </span>
                  <button
                    className="rounded bg-tora text-white px-2 py-1 text-[11px] font-bold hover:bg-tora-accent"
                    onClick={() => addMember(m.id, m.displayName)}
                    disabled={busy}
                  >
                    当日申込として追加
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <input className={`${sel} min-w-[150px]`} placeholder="名前 (必須)" value={gName} onChange={(e) => setGName(e.target.value)} autoFocus />
          <input className={`${sel} min-w-[150px]`} placeholder="カナ" value={gKana} onChange={(e) => setGKana(e.target.value)} />
          <select className={sel} value={gCategory} onChange={(e) => setGCategory(e.target.value)}>
            <option value="">区分を選択</option>
            {categories.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          <input className={`${sel} min-w-[200px] flex-1`} placeholder="メモ (例: 参加（お金いただいた）)" value={gNote} onChange={(e) => setGNote(e.target.value)} />
          <button className="btn-primary text-xs" onClick={addGuest} disabled={busy || !gName.trim()}>
            {busy ? "登録中…" : "登録"}
          </button>
        </div>
      )}
    </div>
  );
}

// 登録済みのゲスト / 当日申込の編集・削除パネル。
// 事前申込はサーバ側で 409 になるため、ここでは当日申込とゲストだけを対象にする。
// 変更した項目だけを送る (受付チェックを触っていないのに true を送ると
// 受付時刻が現在時刻で上書きされてしまうため)。
function EditParticipantPanel({
  eventId,
  target,
  categories,
  onDone,
  onCancel,
  onError,
}: {
  eventId: string;
  target: Participant;
  categories: { code: string; name: string }[];
  onDone: (msg: string) => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}) {
  const isGuest = target.kind === "guest";
  // パネルは一覧の上に出るため、下の方の行で「編集」を押すと画面外に表示され
  // 「押しても何も起きない」ように見える。開いた時点でパネルまでスクロールする。
  // (target ごとに key で再マウントされるので、別の行を開いた時も毎回スクロールする)
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(target.name);
  const [kana, setKana] = useState(target.kana ?? "");
  const [category, setCategory] = useState(target.categoryCode);
  const [mode, setMode] = useState(target.participationMode);
  const [withSocial, setWithSocial] = useState(target.withSocial);
  const [attending, setAttending] = useState(target.attending);
  const [note, setNote] = useState(target.note ?? "");
  const [checkedIn, setCheckedIn] = useState(Boolean(target.checkedInAt));
  const [socialCheckedIn, setSocialCheckedIn] = useState(Boolean(target.socialCheckedInAt));

  const save = async () => {
    const body: Record<string, unknown> = {};
    if (mode !== target.participationMode) body.participationMode = mode;
    if (withSocial !== target.withSocial) body.withSocial = withSocial;
    if (isGuest) {
      if (name.trim() !== target.name) body.name = name.trim();
      if (kana.trim() !== (target.kana ?? "")) body.kana = kana.trim() || null;
      if (category !== target.categoryCode) body.categoryCode = category;
      if (attending !== target.attending) body.attending = attending;
      if (note.trim() !== (target.note ?? "")) body.note = note.trim() || null;
      if (checkedIn !== Boolean(target.checkedInAt)) body.checkedIn = checkedIn;
      if (socialCheckedIn !== Boolean(target.socialCheckedInAt))
        body.socialCheckedIn = socialCheckedIn;
    }
    if (Object.keys(body).length === 0) {
      onCancel();
      return;
    }
    setBusy(true);
    try {
      if (isGuest) {
        await api.patch(`/admin/events/${eventId}/guests/${target.id}`, body);
      } else {
        await api.patch(`/admin/events/${eventId}/applications/${target.applicationId}`, body);
      }
      onDone(`${target.name} さんの登録内容を更新しました`);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const warn = isGuest
      ? `ゲスト「${target.name}」さんを削除しますか？`
      : `当日申込「${target.name}」さんを削除しますか？\n受付済みの場合は受付記録も取り消されます。`;
    if (!confirm(warn)) return;
    setBusy(true);
    try {
      if (isGuest) {
        await api.delete(`/admin/events/${eventId}/guests/${target.id}`);
      } else {
        await api.delete(`/admin/events/${eventId}/applications/${target.applicationId}`);
      }
      onDone(`${target.name} さんを削除しました`);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sel = "rounded border border-black/15 bg-white px-2 py-1 text-xs";
  return (
    <div ref={panelRef} className="mb-3 rounded border border-tora/40 bg-tora/5 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <b className="text-xs">
          {isGuest ? "ゲストを編集" : "当日申込を編集"}: {target.name}
        </b>
        <button className="text-xs underline text-tora-ink/60" onClick={onCancel} disabled={busy}>
          閉じる
        </button>
      </div>

      {isGuest && (
        <div className="flex flex-wrap gap-2">
          <input className={`${sel} min-w-[150px]`} placeholder="名前 (必須)" value={name} onChange={(e) => setName(e.target.value)} />
          <input className={`${sel} min-w-[150px]`} placeholder="カナ" value={kana} onChange={(e) => setKana(e.target.value)} />
          <select className={sel} value={category} onChange={(e) => setCategory(e.target.value)}>
            {categories.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          <input className={`${sel} min-w-[200px] flex-1`} placeholder="メモ" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs">
        <select className={sel} value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="offline">来場</option>
          <option value="online">オンライン</option>
        </select>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={withSocial} onChange={(e) => setWithSocial(e.target.checked)} />
          懇親会に参加
        </label>
        {isGuest && (
          <>
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={attending} onChange={(e) => setAttending(e.target.checked)} />
              参加 (外すと不参加)
            </label>
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={checkedIn} onChange={(e) => setCheckedIn(e.target.checked)} />
              受付済み
            </label>
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={socialCheckedIn} onChange={(e) => setSocialCheckedIn(e.target.checked)} />
              懇親会受付済み
            </label>
          </>
        )}
      </div>

      {!isGuest && (
        <p className="text-[11px] text-tora-ink/60">
          会員の取り違えは「削除」してから登録し直してください。受付の取り消しも削除で行えます。
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-primary text-xs" onClick={save} disabled={busy || (isGuest && !name.trim())}>
          {busy ? "保存中…" : "保存"}
        </button>
        <button
          className="ml-auto rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
          onClick={remove}
          disabled={busy}
        >
          削除
        </button>
      </div>
    </div>
  );
}

function AppTable({
  list,
  onCheckinMember,
  onCheckinGuest,
  onEdit,
}: {
  list: Participant[];
  onCheckinMember?: (memberId: string, name: string) => void;
  onCheckinGuest?: (guestId: string, name: string, checkedIn: boolean) => void;
  /** ゲストと当日申込のみ。事前申込には出さない */
  onEdit?: (p: Participant) => void;
}) {
  if (list.length === 0)
    return <p className="text-sm text-tora-ink/50">該当する参加者はいません</p>;
  const actionable = Boolean(onCheckinMember || onCheckinGuest);
  return (
    <table className="w-full text-xs border border-black/10">
      <thead className="bg-black/5 text-left">
        <tr>
          <th className="p-1.5">区分</th>
          <th className="p-1.5">会員番号</th>
          <th className="p-1.5">名前</th>
          <th className="p-1.5">メール</th>
          <th className="p-1.5">参加方法</th>
          <th className="p-1.5">ステータス</th>
          <th className="p-1.5">受付</th>
          <th className="p-1.5">懇親会</th>
          <th className="p-1.5">懇親受付</th>
          <th className="p-1.5">メモ</th>
          {actionable && <th className="p-1.5">操作</th>}
        </tr>
      </thead>
      <tbody>
        {list.map((p) => {
          const isGuest = p.kind === "guest";
          return (
            <tr key={`${p.kind}:${p.id}`} className="border-t border-black/10">
              <td className="p-1.5 whitespace-nowrap">
                <span
                  className={
                    isGuest
                      ? "rounded bg-amber-100 text-amber-900 px-1.5 py-0.5"
                      : "rounded bg-blue-50 text-blue-900 px-1.5 py-0.5"
                  }
                >
                  {p.categoryLabel}
                </span>
              </td>
              <td className="p-1.5 font-mono">{p.memberNo ?? "-"}</td>
              <td className="p-1.5">
                {p.name}
                {p.kana && (
                  <span className="block text-[10px] text-tora-ink/50 font-normal">
                    {p.kana}
                  </span>
                )}
              </td>
              <td className="p-1.5">{p.email ?? "-"}</td>
              <td className="p-1.5">
                {PARTICIPATION_MODE_LABELS[p.participationMode] ?? p.participationMode}
              </td>
              <td className="p-1.5">
                <StatusBadge tone={STATUS_TONE[p.statusLabel] ?? "neutral"}>
                  {p.statusLabel}
                </StatusBadge>
              </td>
              <td className="p-1.5">{fmtDateTime(p.checkedInAt)}</td>
              <td className="p-1.5">{p.withSocial ? "✓" : "-"}</td>
              <td className="p-1.5">{fmtDateTime(p.socialCheckedInAt)}</td>
              <td className="p-1.5 max-w-[160px] truncate" title={p.note ?? ""}>
                {p.note ?? ""}
              </td>
              {actionable && (
                <td className="p-1.5 whitespace-nowrap">
                  {p.checkedInAt ? (
                    <span className="text-green-600">✓ 受付済</span>
                  ) : isGuest ? (
                    onCheckinGuest && p.attending ? (
                      <button
                        className="rounded bg-tora text-white px-2 py-1 text-[11px] font-bold hover:bg-tora-accent"
                        onClick={() => onCheckinGuest(p.id, p.name, true)}
                      >
                        受付する
                      </button>
                    ) : (
                      <span className="text-tora-ink/30">-</span>
                    )
                  ) : onCheckinMember && p.attending ? (
                    <button
                      className="rounded bg-tora text-white px-2 py-1 text-[11px] font-bold hover:bg-tora-accent"
                      onClick={() => onCheckinMember(p.id, p.name)}
                    >
                      受付する
                    </button>
                  ) : (
                    <span className="text-tora-ink/30">-</span>
                  )}
                  {onEdit && (isGuest || (p.walkIn && p.applicationId)) && (
                    <button
                      className="ml-2 text-[11px] underline text-tora-ink/60"
                      onClick={() => onEdit(p)}
                    >
                      編集
                    </button>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function AdminEventsPage() {
  const [items, setItems] = useState<Row[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState<Form>(INITIAL);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [sendBusy, setSendBusy] = useState(false);
  // MyASP テンプレ用
  const [myaspCfg, setMyaspCfg] = useState<MyaspConfig | null>(null);
  const [myaspScenario, setMyaspScenario] = useState("");
  const [myaspEntryType, setMyaspEntryType] = useState<"event" | "social">("event");
  // 参加者一覧 (会員 + 非会員ゲスト)
  const [appList, setAppList] = useState<AppItem[]>([]);
  const [guestList, setGuestList] = useState<GuestItem[]>([]);
  const [guestCategories, setGuestCategories] = useState<{ code: string; name: string }[]>([]);
  const [appListEventId, setAppListEventId] = useState<string | null>(null);
  const [appLoading, setAppLoading] = useState(false);
  const [appFilter, setAppFilter] = useState<ParticipantFilter>(EMPTY_PARTICIPANT_FILTER);
  // 編集中のゲスト / 当日申込 (null = パネル非表示)
  const [editing, setEditing] = useState<Participant | null>(null);

  // 会員とゲストの統合・絞り込みは shared の関数に任せる (CSV と同じ結果になる)
  const participants = useMemo(
    () => mergeParticipants(appList, guestList),
    [appList, guestList],
  );
  const filteredParticipants = useMemo(
    () => filterParticipants(participants, appFilter),
    [participants, appFilter],
  );

  const load = () =>
    api.get<{ items: Row[] }>("/admin/events").then((r) => setItems(r.items));
  useEffect(() => {
    load();
    api
      .get<{ items: { code: string; name: string }[] }>("/admin/event-guest-categories")
      .then((r) => setGuestCategories(r.items))
      .catch(() => setGuestCategories([]));
    api
      .get<MyaspConfig>("/admin/myasp/config")
      .then(setMyaspCfg)
      .catch(() => {});
  }, []);

  const startCreate = () => {
    setForm(INITIAL);
    setErr(null);
    setMsg(null);
    setOpenForm(true);
  };

  const startEdit = async (id: string) => {
    setErr(null);
    setMsg(null);
    try {
      const d = await api.get<{
        id: string;
        title: string;
        slug: string;
        summary: string | null;
        description: string | null;
        eventStartAt: string | null;
        capacityTotal: number | null;
        status: string;
        thumbnailKey?: string | null;
        notifyLinePreset?: string | null;
        notifyLineAt?: string | null;
        notifiedLineAt?: string | null;
      }>(`/admin/events/${id}`);
      const preset = (d.notifyLinePreset as NotifyPreset) ?? "none";
      setForm({
        id: d.id,
        title: d.title,
        slug: d.slug,
        summary: d.summary ?? "",
        description: d.description ?? "",
        eventStartAt: toDatetimeLocal(d.eventStartAt),
        publishAt: toDatetimeLocal(
          ((d as Record<string, unknown>).publishAt as string) ?? null,
        ),
        capacityTotal: d.capacityTotal == null ? "" : String(d.capacityTotal),
        status: (d.status as Status) ?? "draft",
        participationMode: ((d as Record<string, unknown>).participationMode as PartMode) ?? "offline",
        thumbnailKey: d.thumbnailKey ?? null,
        notifyLinePreset: preset,
        notifyLineCustomAt:
          preset === "custom" ? toDatetimeLocal(d.notifyLineAt ?? null) : "",
        notifyLineAtFromServer: d.notifyLineAt ?? null,
        notifiedLineAt: d.notifiedLineAt ?? null,
        hasSocial: !!(d as Record<string, unknown>).hasSocial,
        socialCapacity: (d as Record<string, unknown>).socialCapacity != null ? String((d as Record<string, unknown>).socialCapacity) : "",
        socialFee: String((d as Record<string, unknown>).socialFee ?? 0),
        socialVenue: ((d as Record<string, unknown>).socialVenue as string) ?? "",
        socialStartAt: toDatetimeLocal(((d as Record<string, unknown>).socialStartAt as string) ?? null),
        cancelDeadlineAt: toDatetimeLocal(((d as Record<string, unknown>).cancelDeadlineAt as string) ?? null),
        surveyNotifyAt: toDatetimeLocal(((d as Record<string, unknown>).surveyNotifyAt as string) ?? null),
      });
      setOpenForm(true);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const closeForm = () => {
    setForm(INITIAL);
    setOpenForm(false);
    setErr(null);
    setMsg(null);
    setAppList([]);
    setAppListEventId(null);
  };

  const loadApplications = async (eventId: string, force = false) => {
    if (!force && appListEventId === eventId) { setAppListEventId(null); return; }
    setAppLoading(true);
    try {
      // 会員とゲストは別テーブルなので 2 本取得し、統合は shared で行う
      const [apps, guests] = await Promise.all([
        api.get<{ items: AppItem[] }>(`/admin/events/${eventId}/applications`),
        api.get<{ items: GuestItem[] }>(`/admin/events/${eventId}/guests`),
      ]);
      setAppList(apps.items);
      setGuestList(guests.items);
      // 別イベントを開いたときに前の絞り込みが残らないようにする。
      // force=true (受付操作後の再読込) では条件を維持する。
      if (!force) {
        setAppFilter(EMPTY_PARTICIPANT_FILTER);
        setEditing(null);
      }
      setAppListEventId(eventId);
    } catch (e) {
      setAppList([]);
      setGuestList([]);
      setErr((e as Error).message);
    }
    finally { setAppLoading(false); }
  };

  // ゲストの受付 / 受付取消
  const guestCheckin = async (guestId: string, name: string, checkedIn: boolean) => {
    if (!appListEventId) return;
    if (!confirm(`${name} さんを受付済みにしますか？`)) return;
    try {
      await api.patch(`/admin/events/${appListEventId}/guests/${guestId}`, { checkedIn });
      await loadApplications(appListEventId, true);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  // 参加者一覧から手動でイベント受付 (QR が読めなかった場合の補助)
  const manualCheckin = async (eventId: string, memberId: string, name: string) => {
    if (!confirm(`${name} さんを受付済みにしますか？`)) return;
    try {
      await api.post("/admin/checkin/manual", { eventId, memberId, checkinType: "event" });
      await loadApplications(eventId, true);
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const submit = async () => {
    setErr(null);
    setMsg(null);
    if (!/^[a-z0-9-]+$/.test(form.slug)) {
      setErr("slug は英小文字・数字・ハイフンのみ");
      return;
    }
    try {
      const payload = {
        title: form.title,
        slug: form.slug,
        summary: form.summary || null,
        description: form.description || null,
        eventStartAt: form.eventStartAt
          ? datetimeLocalToIso(form.eventStartAt)
          : null,
        publishAt: form.publishAt ? datetimeLocalToIso(form.publishAt) : null,
        capacityTotal: form.capacityTotal ? Number(form.capacityTotal) : null,
        status: form.status,
        participationMode: form.participationMode,
        thumbnailKey: form.thumbnailKey,
        notifyLinePreset: form.notifyLinePreset,
        notifyLineCustomAt:
          form.notifyLinePreset === "custom"
            ? datetimeLocalToIso(form.notifyLineCustomAt)
            : null,
        hasSocial: form.hasSocial,
        socialCapacity: form.socialCapacity ? Number(form.socialCapacity) : null,
        socialFee: form.socialFee ? Number(form.socialFee) : 0,
        socialVenue: form.socialVenue || null,
        socialStartAt: form.socialStartAt ? datetimeLocalToIso(form.socialStartAt) : null,
        cancelDeadlineAt: form.cancelDeadlineAt ? datetimeLocalToIso(form.cancelDeadlineAt) : null,
        surveyNotifyAt: form.surveyNotifyAt ? datetimeLocalToIso(form.surveyNotifyAt) : null,
      };
      if (form.id) {
        await api.patch(`/admin/events/${form.id}`, payload);
        setMsg("更新しました");
      } else {
        await api.post("/admin/events", payload);
        setMsg("作成しました");
      }
      setForm(INITIAL);
      setOpenForm(false);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const sendReminderNow = async () => {
    if (!form.id) return;
    setSendBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await api.post<{
        ok: boolean;
        recipientCount: number;
        successCount: number;
        failureCount: number;
        quotaBlocked?: boolean;
      }>(`/admin/events/${form.id}/send-reminder`);
      if (r.quotaBlocked) {
        setErr(
          "今月の送信上限 (soft limit) を超えるため送信しませんでした。quota 残を確認してください。",
        );
      } else if (r.recipientCount === 0) {
        setMsg("対象者が 0 名のため送信されませんでした (申込者の LINE 連携状況を確認)");
      } else {
        setMsg(
          `送信しました: 対象 ${r.recipientCount} 名 / 成功 ${r.successCount} / 失敗 ${r.failureCount}`,
        );
      }
      // リロードして notifiedLineAt 反映
      await startEdit(form.id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSendBusy(false);
    }
  };

  const isEditing = form.id !== null;
  const preview = useMemo(
    () =>
      computeClientPreview(
        form.notifyLinePreset,
        form.eventStartAt,
        form.notifyLineCustomAt,
      ),
    [form.notifyLinePreset, form.eventStartAt, form.notifyLineCustomAt],
  );

  return (
    <div>
      <SectionHeading
        level={2}
        action={
          <button
            className="btn-primary"
            onClick={openForm ? closeForm : startCreate}
          >
            {openForm ? "閉じる" : "新規作成"}
          </button>
        }
      >
        イベント管理
      </SectionHeading>
      {msg && <SuccessBanner>{msg}</SuccessBanner>}
      {openForm && (
        <div className="card p-4 mb-4 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-sm">
              {isEditing ? "編集" : "新規作成"}
            </h3>
            {isEditing && (
              <button
                onClick={() => setForm(INITIAL)}
                className="text-xs underline text-tora-ink/70"
              >
                新規作成に戻る
              </button>
            )}
          </div>
          <div>
            <label className="label">タイトル</label>
            <input
              className="input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div>
            <label className="label">slug (英小文字・数字・ハイフン)</label>
            <input
              className="input"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
            />
          </div>
          <div>
            <label className="label">概要 (一覧用、短文)</label>
            <textarea
              className="input"
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
            />
          </div>
          <div>
            <label className="label">本文 (詳細ページ)</label>
            <RichTextEditor
              value={form.description}
              onChange={(html) => setForm({ ...form, description: html })}
              placeholder="太字・見出し・リンク・画像など自由にレイアウトできます"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">開始日時</label>
              <input
                type="datetime-local"
                className="input"
                value={form.eventStartAt}
                onChange={(e) =>
                  setForm({ ...form, eventStartAt: e.target.value })
                }
              />
            </div>
            <div>
              <label className="label">定員</label>
              <input
                className="input"
                value={form.capacityTotal}
                onChange={(e) =>
                  setForm({ ...form, capacityTotal: e.target.value })
                }
              />
            </div>
          </div>
          <div>
            <label className="label">ステータス</label>
            <select
              className="input"
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value as Status })
              }
            >
              <option value="draft">下書き</option>
              <option value="published">公開</option>
              <option value="closed">締切</option>
              <option value="completed">完了</option>
              <option value="archived">アーカイブ</option>
            </select>
          </div>
          <div>
            <label className="label">公開日時（配信予約・任意）</label>
            <input
              type="datetime-local"
              className="input"
              value={form.publishAt}
              onChange={(e) =>
                setForm({ ...form, publishAt: e.target.value })
              }
            />
            <p className="text-[11px] text-tora-ink/50 mt-1">
              未来の日時を指定すると、その時刻まで会員側に表示されません（ステータスは「公開」にしてください）。空欄なら即時公開です。
            </p>
          </div>
          <div>
            <label className="label">参加方法</label>
            <select
              className="input"
              value={form.participationMode}
              onChange={(e) =>
                setForm({ ...form, participationMode: e.target.value as PartMode })
              }
            >
              <option value="offline">来場のみ</option>
              <option value="online">オンラインのみ</option>
              <option value="hybrid">来場・オンライン 両方可</option>
            </select>
            <p className="text-[11px] text-tora-ink/50 mt-1">
              「両方可」を選ぶと、申込者が来場かオンラインかを選択できます。
            </p>
          </div>
          <ThumbnailUploader
            value={form.thumbnailKey}
            onChange={(k) => setForm({ ...form, thumbnailKey: k })}
          />

          {/* キャンセル締切 */}
          <div>
            <label className="label">キャンセル締切 (空欄=制限なし)</label>
            <input
              type="datetime-local"
              className="input"
              value={form.cancelDeadlineAt}
              onChange={(e) => setForm({ ...form, cancelDeadlineAt: e.target.value })}
            />
          </div>

          {/* 懇親会 */}
          <div className="rounded-lg border border-orange-300/50 bg-orange-50/30 p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.hasSocial}
                onChange={(e) => setForm({ ...form, hasSocial: e.target.checked })}
              />
              <span className="text-sm font-semibold">懇親会あり</span>
            </label>
            {form.hasSocial && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label">懇親会定員</label>
                  <input
                    className="input"
                    value={form.socialCapacity}
                    onChange={(e) => setForm({ ...form, socialCapacity: e.target.value })}
                    placeholder="空欄=無制限"
                  />
                </div>
                <div>
                  <label className="label">参加費 (円)</label>
                  <input
                    className="input"
                    value={form.socialFee}
                    onChange={(e) => setForm({ ...form, socialFee: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">懇親会会場</label>
                  <input
                    className="input"
                    value={form.socialVenue}
                    onChange={(e) => setForm({ ...form, socialVenue: e.target.value })}
                    placeholder="メイン会場と同じ場合は空欄"
                  />
                </div>
                <div>
                  <label className="label">懇親会開始時刻</label>
                  <input
                    type="datetime-local"
                    className="input"
                    value={form.socialStartAt}
                    onChange={(e) => setForm({ ...form, socialStartAt: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* アンケート配信日時 */}
          <div>
            <label className="label">アンケートURL配信日時 (LINE通知)</label>
            <input
              type="datetime-local"
              className="input"
              value={form.surveyNotifyAt}
              onChange={(e) => setForm({ ...form, surveyNotifyAt: e.target.value })}
            />
            <p className="text-[11px] text-tora-ink/50 mt-1">
              この日時に受付済み会員へアンケートURLをLINEで自動配信します。空欄なら配信しません。
            </p>
          </div>

          {/* LINE リマインダー通知 (Phase 2) */}
          <div className="rounded-lg border border-tora/20 bg-tora/5 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-tora">
                LINE リマインダー通知
              </span>
              <span className="text-xs text-tora-ink/60">
                (申込者にイベント前に LINE 配信)
              </span>
            </div>
            <div>
              <label className="label">送信タイミング</label>
              <select
                className="input"
                value={form.notifyLinePreset}
                onChange={(e) =>
                  setForm({
                    ...form,
                    notifyLinePreset: e.target.value as NotifyPreset,
                  })
                }
              >
                {(Object.keys(NOTIFY_PRESET_LABELS) as NotifyPreset[]).map(
                  (p) => (
                    <option key={p} value={p}>
                      {NOTIFY_PRESET_LABELS[p]}
                    </option>
                  ),
                )}
              </select>
            </div>
            {form.notifyLinePreset === "custom" && (
              <div>
                <label className="label">カスタム日時</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={form.notifyLineCustomAt}
                  onChange={(e) =>
                    setForm({ ...form, notifyLineCustomAt: e.target.value })
                  }
                />
              </div>
            )}
            <div className="text-xs text-tora-ink/70">
              <span className="font-semibold">送信予定:</span>{" "}
              {preview}
            </div>

            {/* 送信状態 (編集時のみ) */}
            {isEditing && (
              <>
                {form.notifiedLineAt ? (
                  <Banner tone="info">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span>
                        📨 送信済み: {fmtDateTime(form.notifiedLineAt)}
                      </span>
                      <button
                        type="button"
                        className="btn-outline text-xs ml-auto"
                        onClick={sendReminderNow}
                        disabled={sendBusy}
                      >
                        {sendBusy ? "送信中…" : "再送信する"}
                      </button>
                    </div>
                  </Banner>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-tora-ink/60">
                      未送信 (予定時刻に自動送信されます)
                    </span>
                    <button
                      type="button"
                      className="btn-outline text-xs ml-auto"
                      onClick={sendReminderNow}
                      disabled={sendBusy}
                    >
                      {sendBusy ? "送信中…" : "今すぐ送信"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* MyASP 連携テンプレート (イベント保存後に使用) */}
          {isEditing && form.id && myaspCfg && (
            <div className="rounded-lg border border-tora-ink/20 bg-black/[0.02] p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-tora-ink">
                  MyASP 外部連動 設定テンプレート
                </span>
                <span className="text-xs text-tora-ink/60">
                  (MyASP「外部システムへの連動登録」に貼り付け)
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label">MyASP フォーム URL or item_id</label>
                  <input
                    className="input"
                    value={myaspScenario}
                    onChange={(e) => {
                      // URL が貼られたら item_id を抽出
                      const v = e.target.value.trim();
                      const m = v.match(/\/p\/r\/([A-Za-z0-9]+)/);
                      setMyaspScenario(m ? m[1] : v);
                    }}
                    placeholder="例: 7OHakCcS または https://ashita-kaizen.com/p/r/7OHakCcS"
                  />
                  <p className="text-[11px] text-tora-ink/50 mt-1">
                    URL を貼ると自動で item_id を抽出します
                  </p>
                </div>
                <div>
                  <label className="label">申込種別</label>
                  <select
                    className="input"
                    value={myaspEntryType}
                    onChange={(e) =>
                      setMyaspEntryType(e.target.value as "event" | "social")
                    }
                  >
                    <option value="event">イベント申込</option>
                    <option value="social">懇親会申込</option>
                  </select>
                </div>
              </div>
              {myaspScenario && (() => {
                const tpl = buildMyaspTemplate(
                  myaspCfg,
                  form.id!,
                  myaspScenario,
                  myaspEntryType,
                );
                return (
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs font-semibold text-tora-ink/80 mb-1">
                        ① 外部連動 URL (MyASP に貼り付け)
                      </p>
                      <div
                        className="bg-white border rounded p-2 text-xs font-mono break-all select-all cursor-pointer"
                        onClick={() => navigator.clipboard?.writeText(tpl.url)}
                        title="クリックでコピー"
                      >
                        {tpl.url}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-tora-ink/80 mb-1">
                        ② POST データ (MyASP のデータ欄に貼り付け)
                      </p>
                      <pre
                        className="bg-white border rounded p-2 text-xs font-mono whitespace-pre-wrap select-all cursor-pointer overflow-x-auto"
                        onClick={() => navigator.clipboard?.writeText(tpl.data)}
                        title="クリックでコピー"
                      >
                        {tpl.data}
                      </pre>
                    </div>
                    <p className="text-[11px] text-tora-ink/50">
                      クリックでクリップボードにコピーされます。
                      %xxx% は MyASP 側で自動置換されるテンプレート変数です。
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* 参加者一覧 (編集時) */}
          {isEditing && form.id && (
            <div className="space-y-3">
              <button
                className="btn-outline text-sm"
                onClick={() => loadApplications(form.id!)}
                disabled={appLoading}
              >
                {appLoading ? "読込中…" : appListEventId === form.id ? "参加者一覧を閉じる" : "参加者一覧を表示"}
              </button>
              {appListEventId === form.id && (
                <div className="overflow-x-auto">
                  <AppSummary list={participants} />
                  {appListEventId && (
                    <WalkInPanel
                      eventId={appListEventId}
                      categories={guestCategories}
                      onDone={(m) => { setMsg(m); setErr(null); loadApplications(appListEventId, true); }}
                      onError={setErr}
                    />
                  )}
                  {editing && appListEventId && (
                    <EditParticipantPanel
                      key={`${editing.kind}:${editing.id}`}
                      eventId={appListEventId}
                      target={editing}
                      categories={guestCategories}
                      onDone={(m) => { setMsg(m); setErr(null); setEditing(null); loadApplications(appListEventId, true); }}
                      onCancel={() => setEditing(null)}
                      onError={setErr}
                    />
                  )}
                  <AppFilterBar
                    value={appFilter}
                    onChange={setAppFilter}
                    categories={guestCategories}
                    total={participants.length}
                    shown={filteredParticipants.length}
                    csvHref={`/api/admin/events/${appListEventId}/applications.csv`}
                  />
                  <AppTable
                    list={filteredParticipants}
                    onCheckinMember={(mid, name) =>
                      appListEventId && manualCheckin(appListEventId, mid, name)
                    }
                    onCheckinGuest={guestCheckin}
                    onEdit={setEditing}
                  />
                </div>
              )}
            </div>
          )}

          {err && <ErrorBanner>{err}</ErrorBanner>}
          <button className="btn-primary" onClick={submit}>
            {isEditing ? "更新" : "作成"}
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-black/10">
          <thead className="bg-black/5 text-left">
            <tr>
              <th className="p-2">タイトル</th>
              <th className="p-2">slug</th>
              <th className="p-2">ステータス</th>
              <th className="p-2">参加</th>
              <th className="p-2">開始</th>
              <th className="p-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-black/10">
                <td className="p-2">{e.title}</td>
                <td className="p-2 font-mono text-xs">{e.slug}</td>
                <td className="p-2">
                  <StatusBadge tone={EVENT_STATUS_TONE[e.status] ?? "neutral"}>
                    {EVENT_STATUS_LABELS[e.status] ?? e.status}
                  </StatusBadge>
                </td>
                <td className="p-2 text-xs">
                  <button
                    className="underline text-tora hover:text-tora-accent"
                    onClick={() => loadApplications(e.id)}
                    title="参加者一覧を表示"
                  >
                    <span className="font-semibold">{e.appliedCount ?? 0}</span>
                    {e.capacityTotal != null && <span className="text-tora-ink/50">/{e.capacityTotal}</span>}
                    {e.hasSocial ? (
                      <span className="ml-1 text-tora-ink/50">(懇{e.socialCount ?? 0})</span>
                    ) : null}
                  </button>
                </td>
                <td className="p-2">{fmtDateTime(e.eventStartAt)}</td>
                <td className="p-2 flex items-center gap-2">
                  <button
                    className="text-tora underline text-xs"
                    onClick={() => startEdit(e.id)}
                  >
                    編集
                  </button>
                  <button
                    className="text-xs text-tora-ink/70 hover:text-tora"
                    onClick={() => {
                      const url = `${window.location.origin}/events/${e.id}`;
                      navigator.clipboard?.writeText(url);
                    }}
                    title="会員向けリンクをコピー"
                  >
                    🔗 リンク
                  </button>
                  <Link
                    to={`/admin/events/${e.id}/survey`}
                    className="text-xs text-tora underline"
                    title="振り返りアンケート"
                  >
                    📝 アンケート
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 参加者一覧 (テーブル下展開) */}
      {appListEventId && !openForm && (
        <div className="card p-4 mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-sm">
              参加者一覧: {items.find(i => i.id === appListEventId)?.title ?? ""}
            </h3>
            <button className="text-xs text-tora underline" onClick={() => setAppListEventId(null)}>閉じる</button>
          </div>
          <AppSummary list={participants} />
          <WalkInPanel
            eventId={appListEventId}
            categories={guestCategories}
            onDone={(m) => { setMsg(m); setErr(null); loadApplications(appListEventId, true); }}
            onError={setErr}
          />
          {editing && appListEventId && (
            <EditParticipantPanel
              key={`${editing.kind}:${editing.id}`}
              eventId={appListEventId}
              target={editing}
              categories={guestCategories}
              onDone={(m) => { setMsg(m); setErr(null); setEditing(null); loadApplications(appListEventId, true); }}
              onCancel={() => setEditing(null)}
              onError={setErr}
            />
          )}
          <AppFilterBar
            value={appFilter}
            onChange={setAppFilter}
            categories={guestCategories}
            total={participants.length}
            shown={filteredParticipants.length}
            csvHref={`/api/admin/events/${appListEventId}/applications.csv`}
          />
          <div className="overflow-x-auto">
            <AppTable
              list={filteredParticipants}
              onCheckinMember={(mid, name) =>
                appListEventId && manualCheckin(appListEventId, mid, name)
              }
              onCheckinGuest={guestCheckin}
              onEdit={setEditing}
            />
          </div>
        </div>
      )}
    </div>
  );
}
